import { METHOD, webRequest } from '../../client/HttpClient';
import { API } from '../../client/RouteConstants';
import { IS_FULL_ERROR_CODE } from './constants';
import { buildURL, isGuestUser, logAPIErrorToNewrelic, logAddItemDetailsToNR } from '../utils';
import { ACTION, Analytics } from '../../analytics/analytics';
import { APIResult } from '../APIResult';
import {
    LIST_FULL_ERROR_MESSAGE,
    MONETATE_ITEM_ADDED_FROM_PDP_KEY,
    MONETATE_ITEM_ADDED_FROM_PLP_KEY,
    MONETATE_ITEM_ADDED_KEY,
    MONETATE_ITEM_ADDED_FROM_BASKET_KEY,
    MONETATE_ITEM_ADDED_FROM_OOS_PDP_KEY,
    MONETATE_ITEM_ADDED_FROM_RECS_KEY,
    MONETATE_ITEM_ADDED_FROM_RECS_ERROR_KEY,
    MONETATE_ITEM_ADDED_FROM_GUEST_PDP_KEY,
    MONETATE_ITEM_ADDED_FROM_GUEST_PLP_KEY,
} from '../../../stores/constants';
import { trackEvent } from '../../monetate/monetate';
import {
    trackEvent as AdobetrackEvent,
    AddToWishlist as AdobeAddToWishlist,
} from '@JohnLewisPartnership/jl-analytics-event-stream';

export interface AddItemToWishListRequest {
    skuId: string;
    asafProperties?: ASAFProperties;
}

export const addItemToWishList = async (
    wishListId: string,
    skuId: string,
    itemDetails: Record<string, string>,
    productMetadata?: ProductMetadata,
    mode?: string,
    itemPrice?: string,
    productId?: string,
    isMultipleWishlists = false,
    isAddingFromOOSItem = false,
): Promise<APIResult<WishListItem>> => {
    const url = buildURL(`${API.WISH_LISTS}/${wishListId}?productMode=true&includesImages=true`);
    const addItemRequest: AddItemToWishListRequest = {
        skuId: skuId,
        asafProperties: productMetadata?.asafProperties,
    };

    const request: RequestInit = {
        method: METHOD.POST,
        headers: {
            'Content-Type': 'application/json',
            'x-api-client-id': 'add-to-wishlist-esi',
        },
        body: JSON.stringify(addItemRequest),
        credentials: 'include',
    };

    const res = await webRequest(url, request);

    const isAddingFromPLP = mode === 'icon-placeholder';
    const isAddingFromBasket = mode === 'icon-placeholder-basket';
    const isAddingFromPDPButton = mode === 'button';
    const isAddingFromPDPIcon = mode === 'icon-placeholder-pdp';
    const isAddingFromRecs = mode === 'icon-placeholder-wishlist-recs';
    const isAddingFromGuestPDP = (isAddingFromPDPButton || isAddingFromPDPIcon) && isGuestUser();
    const isAddingFromGuestPLP = isAddingFromPLP && isGuestUser();

    const result = new APIResult<WishListItem>();
    if (!res) {
        logAPIErrorToNewrelic(`addItemToWishList`, `Empty response.`, {
            ...request,
            url: url,
            errorMessage: result.errorMessage,
            showError: result.showError,
            result: result,
            response: res,
        });
        return result;
    }

    if (res.ok) {
        if (isAddingFromPDPButton || isAddingFromPDPIcon || isAddingFromOOSItem) {
            Analytics.dispatchAdobeAnalytics(
                ACTION.ADD_TO_WISH_LIST,
                undefined,
                undefined,
                'pdp',
                'add to wishlist',
                {},
                {
                    productId: productId,
                    itemPrice: itemPrice,
                    qty: '1',
                    sku: skuId,
                },
            );
            // new analytic event
            AdobetrackEvent(AdobeAddToWishlist, {
                triggeringImplementationId: 'add-to-wishlist-component',
                originatingImplementationId: 'add-to-wishlist-component',
                type: 'pdp',
                modalStep: 'select a list',
                products: [
                    {
                        price: {
                            now: itemPrice ? parseFloat(itemPrice) : 0,
                        },
                        id: productId || '',
                        sku: skuId,
                        quantity: 1,
                    },
                ],

                page: {
                    name: window.jlData?.page?.pageInfo?.pageName,
                    type: window.jlData?.page?.pageInfo?.pageType,
                },
                basket: {
                    products: [],
                    subTotal: 0,
                    productCount: 0,
                    quantity: 0,
                },
            });
        }
        trackEvent(MONETATE_ITEM_ADDED_KEY); // Global event tracked for additions from any of PLP, PDP or basket
        const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:SUCCESS', {
            detail: {
                isMultipleWishlists: isMultipleWishlists,
                code: 'wishlist success',
                wishListId,
            },
        });
        window.dispatchEvent(customEvent);

        if (isGuestUser()) {
            const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:GUEST_SUCCESS', {
                detail: {
                    isMultipleWishlists: isMultipleWishlists,
                    code: 'guest wishlist success',
                    wishListId,
                },
            });
            window.dispatchEvent(customEvent);
        }

        isAddingFromPLP && trackEvent(MONETATE_ITEM_ADDED_FROM_PLP_KEY); // Track individual additions from PLP
        isAddingFromBasket && trackEvent(MONETATE_ITEM_ADDED_FROM_BASKET_KEY); // Track individual additions from Basket
        (isAddingFromPDPButton || isAddingFromPDPIcon) && trackEvent(MONETATE_ITEM_ADDED_FROM_PDP_KEY); // Track individual additions from PDP
        isAddingFromRecs && trackEvent(MONETATE_ITEM_ADDED_FROM_RECS_KEY); // Track individual additions from recs within wishlist success
        isAddingFromOOSItem && trackEvent(MONETATE_ITEM_ADDED_FROM_OOS_PDP_KEY); // this button is only shown on OOS variants so all clicks are from OOS
        isAddingFromGuestPDP && trackEvent(MONETATE_ITEM_ADDED_FROM_GUEST_PDP_KEY); // track guest additions from a PDP
        isAddingFromGuestPLP && trackEvent(MONETATE_ITEM_ADDED_FROM_GUEST_PLP_KEY); // track guest additions from a PDP

        result.success = true;
        result.data = await res.json();
        logAddItemDetailsToNR(itemDetails);
    } else {
        const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:ERROR', {
            detail: {
                code: 'wishlist add error',
            },
        });
        window.dispatchEvent(customEvent);

        isAddingFromRecs && trackEvent(MONETATE_ITEM_ADDED_FROM_RECS_ERROR_KEY); // Track individual additions from recs within wishlist error
        logAPIErrorToNewrelic(`addItemToWishList`, `Response Not OK`, { ...res, request: request, url: url });
    }

    if (res.status === 401) {
        result.showError = false;
        logAPIErrorToNewrelic(`addItemToWishList`, `UNAUTHORISED - 401`, {
            ...res,
            request: request,
            url: url,
            result: result,
            errorMessage: result.errorMessage,
            showError: result.showError,
            response: res,
        });
    }

    if (res.status == 400) {
        const errorResponse: WishListAPIError = await res.json();
        // errorResponse.response will always return an array of 1 item.
        if (errorResponse.response[0].statusCode === IS_FULL_ERROR_CODE) {
            logAPIErrorToNewrelic(`addItemToWishList`, `BAD_REQUEST - 400 [List Full]`, {
                ...res,
                request: request,
                url: url,
                result: result,
                errorMessage: result.errorMessage,
                showError: result.showError,
                response: res,
            });
            result.errorMessage = LIST_FULL_ERROR_MESSAGE;
        }
    }

    return result;
};
